import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Activity,
  AlertTriangle,
  Ban,
  CheckCircle2,
  Clock3,
  ExternalLink,
  FileClock,
  FileSearch,
  Gauge,
  KeyRound,
  Mail,
  RefreshCw,
  RotateCcw,
  Search,
  Send,
  Shield,
  XCircle,
  ShieldCheck,
  UserCheck,
  Users,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import { Input } from '@/components/ui/input';

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';


import ContractorOnboardingAuditDashboard from '@/components/contractor-onboarding/ContractorOnboardingAuditDashboard';
import ContractorSetupWizard from '@/components/contractor-onboarding/ContractorSetupWizard';

import {
  activateOperationalDelivery,
  deactivateOperationalDelivery,
  getActivationPreview,
  getDeactivationPreview,
  getOnboardingOverview,
  getCandidateReview,
  openCandidateEvidence,
  previewEligibility,
  resendPreview,
  reviewCandidateEvidence,
  createEvidenceReplacementRequest,
  createCandidateInvitation,
  createAndSendCandidateInvitation,
  approveOnboardingCandidate,
  revokeInvitation,
} from '@/api/contractorOnboardingAdminApi';


/*
 * IRIS_R9_R1_PRINCIPAL_COMPLIANCE_PROJECTION
 *
 * Historical drafts may still contain fields from the retired
 * criminal-history / conflict-of-interest disclosure workflow.
 *
 * They are preserved in storage for audit/history, but are not part
 * of the current candidate workflow and therefore must not appear in
 * the principal's canonical Review presentation.
 */
const PRINCIPAL_REVIEW_HIDDEN_COMPLIANCE_KEYS =
  new Set([
    'criminal_history_disclosed',
    'criminal_history_notes',
    'conflicts_disclosed',
    'conflict_notes',
  ]);


function principalReviewComplianceEntries(
  compliance
) {
  return Object.entries(
    compliance || {}
  ).filter(
    ([key]) =>
      !PRINCIPAL_REVIEW_HIDDEN_COMPLIANCE_KEYS
        .has(key)
  );
}


const statusClasses = {
  issued:
    'bg-blue-500/10 text-blue-400 border-blue-500/20',

  opened:
    'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',

  in_progress:
    'bg-amber-500/10 text-amber-400 border-amber-500/20',

  completed:
    'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',

  revoked:
    'bg-red-500/10 text-red-400 border-red-500/20',

  replaced:
    'bg-purple-500/10 text-purple-400 border-purple-500/20',

  expired:
    'bg-slate-500/10 text-slate-400 border-slate-500/20',

  sent:
    'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',

  pending:
    'bg-amber-500/10 text-amber-400 border-amber-500/20',

  failed:
    'bg-red-500/10 text-red-400 border-red-500/20',

  active:
    'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',

  accepted:
    'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',

  rejected:
    'bg-red-500/10 text-red-400 border-red-500/20',

  review_required:
    'bg-amber-500/10 text-amber-400 border-amber-500/20',

  evidence_issue:
    'bg-red-500/10 text-red-400 border-red-500/20',

  ready_for_approval:
    'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',

  missing:
    'bg-red-500/10 text-red-400 border-red-500/20',
};


const formatDate = (value) => {
  if (!value) {
    return '—';
  }

  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};


const StatusBadge = ({ value }) => {
  const status = value || 'unknown';

  return (
    <Badge
      variant="outline"
      className={
        statusClasses[status]
        || 'bg-slate-500/10 text-slate-400 border-slate-500/20'
      }
    >
      {String(status).replaceAll('_', ' ')}
    </Badge>
  );
};


const MetricCard = ({
  title,
  value,
  description,
  icon: Icon,
}) => (
  <Card>
    <CardHeader className="pb-2">
      <div className="flex items-center justify-between">
        <CardDescription>
          {title}
        </CardDescription>

        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
    </CardHeader>

    <CardContent>
      <div className="text-2xl font-bold">
        {value}
      </div>

      <p className="mt-1 text-xs text-muted-foreground">
        {description}
      </p>
    </CardContent>
  </Card>
);



/*
 * IRIS_R6_F3_R3_ELIGIBILITY_REQUEST
 *
 * Principal-facing operational identity eligibility.
 * READ ONLY.
 *
 * No provisioning POST exists in this helper.
 */

/*
 * IRIS_R6_F4_PROVISION_REQUEST
 *
 * Explicit principal transaction.
 * This is the ONLY frontend operational identity
 * provisioning call.
 */
async function provisionOperationalIdentity(
  invitationId
) {
  if (!invitationId) {
    throw new Error(
      'Candidate invitation identity is unavailable.'
    );
  }

  const token =
    localStorage.getItem('access_token') || '';

  const base =
    '';

  const response = await fetch(
    `${base}/api/contractor-onboarding/admin-console/candidates/${encodeURIComponent(
      invitationId
    )}/provision-operational-identity`,
    {
      method: 'POST',

      headers: {
        Authorization:
          `Bearer ${token}`,

        Accept:
          'application/json',

        'Content-Type':
          'application/json',
      },

      body: JSON.stringify({
        confirm: true,
      }),
    }
  );

  let payload = null;

  try {
    payload = await response.json();
  } catch (error) {
    payload = null;
  }

  if (!response.ok) {
    const detail =
      payload?.detail;

    throw new Error(
      typeof detail === 'string'
        ? detail
        : (
            detail?.message
            || `Operational identity provisioning failed (${response.status})`
          )
    );
  }

  return payload;
}


async function getOperationalIdentityEligibility(
  invitationId
) {
  if (!invitationId) {
    throw new Error(
      'Candidate invitation identity is unavailable.'
    );
  }

  const token =
    localStorage.getItem('access_token') || '';

  const base =
    '';

  const response = await fetch(
    `${base}/api/contractor-onboarding/admin-console/candidates/${encodeURIComponent(
      invitationId
    )}/operational-identity-eligibility`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    }
  );

  let payload = null;

  try {
    payload = await response.json();
  } catch (error) {
    payload = null;
  }

  if (!response.ok) {
    const detail = payload?.detail;

    const message =
      typeof detail === 'string'
        ? detail
        : (
            detail?.message
            || `Operational identity eligibility request failed (${response.status})`
          );

    throw new Error(message);
  }

  return payload;
}


export default function ContractorOnboardingAdmin() {

  const currentUserRole = (() => {
    try {
      return JSON.parse(
        localStorage.getItem('user') || '{}'
      )?.role || '';
    } catch {
      return '';
    }
  })();

  const canManageDeliveryControls = (
    currentUserRole === 'super_admin'
  );


  const [data, setData] = useState(null);

  // IRIS_R6_F3_R3_STATE
  const [
    operationalEligibility,
    setOperationalEligibility,
  ] = useState(null);

  const [
    operationalEligibilityLoading,
    setOperationalEligibilityLoading,
  ] = useState(false);

  const [
    operationalEligibilityError,
    setOperationalEligibilityError,
  ] = useState('');

  const [
    operationalActivationPreviewOpen,
    setOperationalActivationPreviewOpen,
  ] = useState(false);


  // IRIS_R6_F4_PROVISION_STATE
  const [
    operationalProvisioning,
    setOperationalProvisioning,
  ] = useState(false);

  const [
    operationalProvisioningError,
    setOperationalProvisioningError,
  ] = useState('');

  const [
    operationalProvisioningResult,
    setOperationalProvisioningResult,
  ] = useState(null);


  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');

  const [
    selectedContractor,
    setSelectedContractor,
  ] = useState('');

  const [
    eligibilityEmail,
    setEligibilityEmail,
  ] = useState('');

  const [
    eligibility,
    setEligibility,
  ] = useState(null);

  const [
    eligibilityLoading,
    setEligibilityLoading,
  ] = useState(false);

  const [
    previewDialog,
    setPreviewDialog,
  ] = useState(null);

  const [
    revokeTarget,
    setRevokeTarget,
  ] = useState(null);

  const [
    revokeReason,
    setRevokeReason,
  ] = useState('');

  const [
    resendResult,
    setResendResult,
  ] = useState(null);

  // ================================================================
  // V1.0C-H-R10-C ADMIN REVIEW STATE
  // ================================================================

  const [
    reviewDialog,
    setReviewDialog,
  ] = useState(null);

  const [
    reviewLoading,
    setReviewLoading,
  ] = useState(false);

  const [
    evidenceAction,
    setEvidenceAction,
  ] = useState(null);

  const [
    evidenceNotes,
    setEvidenceNotes,
  ] = useState('');

  const [
    evidenceSubmitting,
    setEvidenceSubmitting,
  ] = useState(false);


  // V1.0C-H-R10-J-R2 INVITE CANDIDATE STATE
  const [
    inviteDialogOpen,
    setInviteDialogOpen,
  ] = useState(false);

  const [
    inviteContractorId,
    setInviteContractorId,
  ] = useState('');

  const [
    inviteCandidateName,
    setInviteCandidateName,
  ] = useState('');

  const [
    inviteCandidateEmail,
    setInviteCandidateEmail,
  ] = useState('');

  const [
    inviteTtlHours,
    setInviteTtlHours,
  ] = useState('72');

  const [
    inviteSubmitting,
    setInviteSubmitting,
  ] = useState(false);

  const [
    inviteResult,
    setInviteResult,
  ] = useState(null);

  const [
    candidateApproving,
    setCandidateApproving,
  ] = useState(false);

  const loadOverview = useCallback(
    async () => {
      setLoading(true);
      setError('');

      try {
        const result = await (
          getOnboardingOverview()
        );

        setData(result);

        if (
          !selectedContractor
          && result.contractors?.length
        ) {
          setSelectedContractor(
            result.contractors[0].id
          );
        }
      } catch (err) {
        setError(
          err?.response?.data?.detail
          || err?.message
          || 'Unable to load Contractor Onboarding console'
        );
      } finally {
        setLoading(false);
      }
    },
    [selectedContractor]
  );

  useEffect(
    () => {
      loadOverview();
    },
    [loadOverview]
  );

  const invitations = useMemo(
    () => {
      const rows = data?.invitations || [];

      const q = search.trim().toLowerCase();

      if (!q) {
        return rows;
      }

      return rows.filter(
        (row) => (
          String(
            row.candidate_name || ''
          )
            .toLowerCase()
            .includes(q)
          ||
          String(
            row.candidate_email || ''
          )
            .toLowerCase()
            .includes(q)
          ||
          String(
            row.status || ''
          )
            .toLowerCase()
            .includes(q)
          ||
          String(
            row.id || ''
          )
            .toLowerCase()
            .includes(q)
        )
      );
    },
    [data, search]
  );

  const candidates = useMemo(
    () => {
      const rows = data?.candidates || [];

      const q = search.trim().toLowerCase();

      if (!q) {
        return rows;
      }

      return rows.filter(
        (row) => (
          String(
            row.candidate_name || ''
          )
            .toLowerCase()
            .includes(q)
          ||
          String(
            row.candidate_email || ''
          )
            .toLowerCase()
            .includes(q)
          ||
          String(
            row.contractor_name || ''
          )
            .toLowerCase()
            .includes(q)
        )
      );
    },
    [data, search]
  );

  const operationalEnabled = Boolean(
    data?.operational?.enabled
  );

  const masterEnabled = Boolean(
    data?.transport_master_enabled
  );

  const systemReady = (
    operationalEnabled
    && masterEnabled
  );

  const previewActivation = async () => {
    try {
      const result = operationalEnabled
        ? await getDeactivationPreview()
        : await getActivationPreview();

      setPreviewDialog({
        type:
          operationalEnabled
            ? 'deactivate'
            : 'activate',

        result,
        reason: '',
        confirmation: '',
      });
    } catch (err) {
      setError(
        err?.response?.data?.detail
        || err?.message
        || 'Unable to load delivery preview'
      );
    }
  };

  const executeOperationalChange = async () => {
    if (!previewDialog) {
      return;
    }

    setError('');

    try {
      if (
        previewDialog.type
        === 'activate'
      ) {
        await activateOperationalDelivery(
          previewDialog.reason,
          previewDialog.confirmation
        );
      } else {
        await deactivateOperationalDelivery(
          previewDialog.reason,
          previewDialog.confirmation
        );
      }

      setPreviewDialog(null);

      await loadOverview();
    } catch (err) {
      setError(
        err?.response?.data?.detail
        || err?.message
        || 'Operational control change failed'
      );
    }
  };

  const runEligibility = async () => {
    if (
      !selectedContractor
      || !eligibilityEmail.trim()
    ) {
      return;
    }

    setEligibilityLoading(true);
    setEligibility(null);
    setError('');

    try {
      const result = await previewEligibility(
        selectedContractor,
        eligibilityEmail.trim()
      );

      setEligibility(result);
    } catch (err) {
      setError(
        err?.response?.data?.detail
        || err?.message
        || 'Eligibility preview failed'
      );
    } finally {
      setEligibilityLoading(false);
    }
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) {
      return;
    }

    setError('');

    try {
      await revokeInvitation(
        revokeTarget.contractor_id,
        revokeTarget.id,
        revokeReason.trim() || 'Revoked from IRIS admin console'
      );

      setRevokeTarget(null);
      setRevokeReason('');

      await loadOverview();
    } catch (err) {
      setError(
        err?.response?.data?.detail
        || err?.message
        || 'Invitation revoke failed'
      );
    }
  };

  const runResendPreview = async (
    invitation
  ) => {
    setError('');
    setResendResult(null);

    try {
      const result = await resendPreview(
        invitation.contractor_id,
        invitation.id
      );

      setResendResult({
        invitation,
        result,
      });
    } catch (err) {
      setError(
        err?.response?.data?.detail
        || err?.message
        || 'Resend preview failed'
      );
    }
  };


  // ================================================================
  // V1.0C-H-R10-C ADMIN REVIEW HANDLERS
  // ================================================================

  const openCandidateReview = async (
    candidate
  ) => {
    // IRIS_R6_F3_R3_REVIEW_RESET
    setOperationalEligibility(null);
    setOperationalEligibilityError('');
    setOperationalActivationPreviewOpen(false);

    setReviewLoading(true);
    setError('');

    try {
      const result = await getCandidateReview(
        candidate.invitation_id
      );

      if (!result) {
        throw new Error(
          'Candidate review returned no data'
        );
      }

      setReviewDialog(
        result
      );
    } catch (err) {
      console.error(
        'IRIS candidate review failed',
        err
      );

      setError(
        err?.response?.data?.detail
        || err?.message
        || 'Unable to load candidate review'
      );
    } finally {
      setReviewLoading(false);
    }
  };



  /*
   * IRIS_R6_F3_R3_ELIGIBILITY_LOADER
   *
   * Authoritative principal review identity:
   * reviewDialog.invitation.id
   *
   * READ ONLY.
   */
  const loadOperationalEligibility = async () => {
    const invitationId =
      reviewDialog?.invitation?.id;

    if (!invitationId) {
      setOperationalEligibility(null);

      setOperationalEligibilityError(
        'Candidate invitation identity is unavailable.'
      );

      return;
    }

    setOperationalEligibilityLoading(true);
    setOperationalEligibilityError('');

    try {
      const result =
        await getOperationalIdentityEligibility(
          invitationId
        );

      setOperationalEligibility(result);
    } catch (error) {
      setOperationalEligibility(null);

      setOperationalEligibilityError(
        error?.message
        || 'Unable to verify operational identity eligibility.'
      );
    } finally {
      setOperationalEligibilityLoading(false);
    }
  };



  /*
   * IRIS_R6_F4_PROVISION_HANDLER
   *
   * Explicit principal action only.
   * NEW_IDENTITY is revalidated by the backend.
   */
  const confirmOperationalIdentityProvisioning =
    async () => {

      const invitationId =
        reviewDialog?.invitation?.id;

      if (!invitationId) {
        setOperationalProvisioningError(
          'Candidate invitation identity is unavailable.'
        );

        return;
      }


      if (
        operationalEligibility
          ?.classification
        !== 'NEW_IDENTITY'
      ) {
        setOperationalProvisioningError(
          'Operational identity eligibility must be NEW_IDENTITY.'
        );

        return;
      }


      setOperationalProvisioning(true);
      setOperationalProvisioningError('');


      try {

        const result =
          await provisionOperationalIdentity(
            invitationId
          );


        if (
          result?.rapidx_handoff?.status
          !== 'ready_for_rapidx'
        ) {
          throw new Error(
            'Operational identity was provisioned but the Rapid X handoff is not ready.'
          );
        }


        setOperationalProvisioningResult(
          result
        );


        setOperationalActivationPreviewOpen(
          false
        );


        await loadOperationalEligibility();


      } catch (error) {

        setOperationalProvisioningError(
          error?.message
          || 'Unable to provision operational identity.'
        );

      } finally {

        setOperationalProvisioning(false);

      }
    };


  const refreshCandidateReview = async () => {
    const invitationId =
      reviewDialog?.invitation?.id;

    if (!invitationId) {
      return;
    }

    const result = await getCandidateReview(
      invitationId
    );

    setReviewDialog(
      result
    );
  };


  const viewEvidence = async (
    evidence
  ) => {
    try {
      await openCandidateEvidence(
        reviewDialog?.contractor?.id,
        evidence.id,
        evidence.original_filename
      );
    } catch (err) {
      setError(
        err?.response?.data?.detail
        || err?.message
        || 'Unable to open evidence'
      );
    }
  };


  // V1.0C-H-R10-J-R2 INVITE CANDIDATE HANDLERS

  const openInviteCandidate = () => {
    const contractors =
      data?.contractors || [];

    setInviteContractorId(
      selectedContractor
      || contractors?.[0]?.id
      || ''
    );

    setInviteCandidateName('');
    setInviteCandidateEmail('');
    setInviteTtlHours('72');
    setInviteResult(null);
    setError('');
    setInviteDialogOpen(true);
  };


  const submitCandidateInvitation = async () => {
    const contractorId =
      inviteContractorId.trim();

    const candidateName =
      inviteCandidateName.trim();

    const candidateEmail =
      inviteCandidateEmail
        .trim()
        .toLowerCase();

    const ttlHours =
      Number(inviteTtlHours);

    if (!contractorId) {
      setError(
        'Select a contractor.'
      );
      return;
    }

    if (!candidateName) {
      setError(
        'Candidate name is required.'
      );
      return;
    }

    if (
      !candidateEmail
      || !candidateEmail.includes('@')
    ) {
      setError(
        'Enter a valid candidate email.'
      );
      return;
    }

    if (
      !Number.isInteger(ttlHours)
      || ttlHours < 1
      || ttlHours > 168
    ) {
      setError(
        'Invitation expiry must be between 1 and 168 hours.'
      );
      return;
    }

    setInviteSubmitting(true);
    setInviteResult(null);
    setError('');

    try {
      const result =
        await createAndSendCandidateInvitation(
          contractorId,
          candidateName,
          candidateEmail,
          ttlHours
        );

      if (
        result?.mode !== 'preview_only'
        || result?.email_sent !== false
        || !result?.invitation?.id
      ) {
        throw new Error(
          'Rapid X did not confirm preview-only invitation creation.'
        );
      }

      const safeResult = {
        ...result,
      };

      delete safeResult.raw_token;

      setInviteResult(safeResult);

      await loadOverview();

      setInviteDialogOpen(false);
      setInviteCandidateName('');
      setInviteCandidateEmail('');
      setInviteTtlHours('72');
      setInviteResult(null);

    } catch (err) {
      setError(
        err?.response?.data?.detail
        || err?.message
        || 'Unable to create invitation'
      );

    } finally {
      setInviteSubmitting(false);
    }
  };


  const beginEvidenceReview = (
    evidence,
    status
  ) => {
    setEvidenceAction({
      evidence,
      status,
    });

    setEvidenceNotes(
      evidence.review_notes || ''
    );
  };


  const submitEvidenceReview = async () => {
    if (
      !evidenceAction
      || !reviewDialog?.contractor?.id
      || !reviewDialog?.invitation?.id
    ) {
      return;
    }

    if (
      evidenceAction.status === 'rejected'
      && !evidenceNotes.trim()
    ) {
      setError(
        'A rejection reason is required.'
      );
      return;
    }

    //
    // V1.0C-H-R10-K-R3
    //
    // Capture stable identifiers before mutating dialog state.
    // The evidence-review POST changes authoritative backend state.
    // Immediately reload the full candidate-review projection using
    // the captured invitation ID rather than depending on React state
    // during the subsequent asynchronous refresh.
    //
    const contractorId =
      reviewDialog.contractor.id;

    const invitationId =
      reviewDialog.invitation.id;

    const evidenceId =
      evidenceAction.evidence.id;

    const requestedStatus =
      evidenceAction.status;

    const reviewNotes =
      evidenceNotes.trim();

    setEvidenceSubmitting(true);
    setError('');

    try {
      await reviewCandidateEvidence(
        contractorId,
        evidenceId,
        requestedStatus,
        reviewNotes
      );

      //
      // Fetch the authoritative projection directly.
      //
      // This response contains:
      // - current evidence statuses
      // - requirements
      // - accepted_required_count
      // - all_required_accepted
      // - ready_for_approval state
      //
      const refreshedReview =
        await getCandidateReview(
          invitationId
        );

      if (!refreshedReview) {
        throw new Error(
          'Evidence was saved, but the refreshed candidate review returned no data.'
        );
      }

      setReviewDialog(
        refreshedReview
      );

      setEvidenceAction(null);
      setEvidenceNotes('');

      await loadOverview();

    } catch (err) {
      setError(
        err?.response?.data?.detail
        || err?.message
        || 'Evidence review failed'
      );

    } finally {
      setEvidenceSubmitting(false);
    }
  };


  // V1.0C-H-R10-D-R1 CREATE REPLACEMENT LINK
  const createReplacementLink = async (
    evidence
  ) => {
    if (
      !reviewDialog?.contractor?.id
      || !evidence?.id
    ) {
      return;
    }

    setError('');

    try {
      const result =
        await createEvidenceReplacementRequest(
          reviewDialog.contractor.id,
          evidence.id
        );

      if (!result?.replacement_url) {
        throw new Error(
          'Replacement link was not returned'
        );
      }

      window.prompt(
        'Secure replacement link — copy and send this to the candidate:',
        result.replacement_url
      );

      await refreshCandidateReview();

    } catch (err) {
      setError(
        err?.response?.data?.detail
        || err?.message
        || 'Unable to create replacement link'
      );
    }
  };


  // ================================================================
  // V1.0C-H-R10-E-R1 FINAL CANDIDATE APPROVAL
  // ================================================================

  const approveCandidateFinal = async () => {

    const invitationId =
      reviewDialog?.invitation?.id;

    if (
      !invitationId
      || !reviewDialog?.review?.all_required_accepted
      || reviewDialog?.approval
    ) {
      return;
    }

    const confirmed = window.confirm(
      'Approve this candidate for contractor workforce admission? '
      + 'This creates an immutable approval record based on the '
      + 'completed application, signed acknowledgement and accepted '
      + 'current evidence. It does not create login or roster access.'
    );

    if (!confirmed) {
      return;
    }

    setCandidateApproving(true);
    setError('');

    try {
      await approveOnboardingCandidate(
        invitationId
      );

      await refreshCandidateReview();
      await loadOverview();

    } catch (err) {
      setError(
        err?.response?.data?.detail
        || err?.message
        || 'Final candidate approval failed'
      );

    } finally {
      setCandidateApproving(false);
    }
  };


  if (loading && !data) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <RefreshCw className="mx-auto h-8 w-8 animate-spin text-cyan-400" />

          <p className="mt-3 text-sm text-muted-foreground">
            Loading Contractor Onboarding control plane…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1 sm:p-2">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400">
            <ShieldCheck className="h-4 w-4" />
            IRIS Contractor Onboarding
          </div>

          <h1 className="text-3xl font-bold tracking-tight">
            Contractor Onboarding
          </h1>

          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            Invitation lifecycle, candidate progress and delivery controls
            for contractor onboarding through Rapid X.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={loadOverview}
            disabled={loading}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`}
            />

            Refresh
          </Button>

          <Button
            variant="outline"
            onClick={() => {
              window.open(
                data?.public_onboarding_url
                || 'https://onboarding.rapidxtech.com.au',
                '_blank',
                'noopener,noreferrer'
              );
            }}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Public Portal
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-400">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          title="Contractors"
          value={data?.counts?.contractors ?? 0}
          description="Onboarding contractors"
          icon={Users}
        />

        <MetricCard
          title="Invitations"
          value={data?.counts?.invitations ?? 0}
          description="Invitation ledger records"
          icon={Mail}
        />

        <MetricCard
          title="Candidate Sessions"
          value={data?.counts?.candidate_sessions ?? 0}
          description="Secure onboarding sessions"
          icon={UserCheck}
        />

        <MetricCard
          title="Invitation Sends"
          value={data?.mail?.invitation_sends ?? 0}
          description="Audited successful sends"
          icon={Send}
        />
      </div>

      <Card
        className={
          systemReady
            ? 'border-emerald-500/30'
            : 'border-amber-500/30'
        }
      >
        <CardContent className="pt-6">
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="flex items-start gap-3">
              <div
                className={
                  `rounded-lg p-2 ${
                    masterEnabled
                      ? 'bg-emerald-500/10 text-emerald-400'
                      : 'bg-amber-500/10 text-amber-400'
                  }`
                }
              >
                <KeyRound className="h-5 w-5" />
              </div>

              <div>
                <p className="text-sm font-semibold">
                  Transport Master Gate
                </p>

                <p className="mt-1 text-sm text-muted-foreground">
                  {masterEnabled ? 'Enabled' : 'Locked'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div
                className={
                  `rounded-lg p-2 ${
                    operationalEnabled
                      ? 'bg-emerald-500/10 text-emerald-400'
                      : 'bg-amber-500/10 text-amber-400'
                  }`
                }
              >
                <Shield className="h-5 w-5" />
              </div>

              <div>
                <p className="text-sm font-semibold">
                  Operational Gate
                </p>

                <p className="mt-1 text-sm text-muted-foreground">
                  {operationalEnabled ? 'Enabled' : 'Disabled'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div
                className={
                  `rounded-lg p-2 ${
                    systemReady
                      ? 'bg-emerald-500/10 text-emerald-400'
                      : 'bg-slate-500/10 text-slate-400'
                  }`
                }
              >
                <Activity className="h-5 w-5" />
              </div>

              <div>
                <p className="text-sm font-semibold">
                  Production Delivery
                </p>

                <p className="mt-1 text-sm text-muted-foreground">
                  {
                    systemReady
                      ? 'Operationally available'
                      : 'Not active'
                  }
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs
        defaultValue="overview"
        className="space-y-4"
      >
        <TabsList className="flex h-auto flex-wrap justify-start">
          <TabsTrigger value="overview">
            Overview
          </TabsTrigger>
          <TabsTrigger value="setup">
            Contractor Setup
          </TabsTrigger>

          <TabsTrigger value="invitations">
            Invitations
          </TabsTrigger>

          <TabsTrigger value="candidates">
            Candidates
          </TabsTrigger>

          {canManageDeliveryControls && (
            <TabsTrigger value="delivery">
              Delivery Controls
            </TabsTrigger>
          )}

          <TabsTrigger value="audit-dashboard">
            Audit Dashboard
          </TabsTrigger>

          <TabsTrigger value="audit">
            Audit History
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value="setup"
          className="mt-6"
        >
          <ContractorSetupWizard />
        </TabsContent>

        <TabsContent value="overview">
          <div className="grid gap-4 xl:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>
                  Contractor Portfolio
                </CardTitle>

                <CardDescription>
                  Contractors currently available to the onboarding engine.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-3">
                {(data?.contractors || []).map(
                  (contractor) => (
                    <div
                      key={contractor.id}
                      className="rounded-lg border p-4"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <p className="font-semibold">
                            {
                              contractor.trading_name
                              || contractor.legal_name
                            }
                          </p>

                          <p className="mt-1 text-sm text-muted-foreground">
                            {contractor.legal_name}
                          </p>

                          <p className="mt-2 text-xs text-muted-foreground">
                            Contractor ID: {contractor.id}
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <StatusBadge
                            value={contractor.status}
                          />

                          <Badge variant="outline">
                            {
                              contractor.onboarding_enabled
                                ? 'Onboarding enabled'
                                : 'Onboarding disabled'
                            }
                          </Badge>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  Invitation Lifecycle
                </CardTitle>

                <CardDescription>
                  Current state distribution across the invitation ledger.
                </CardDescription>
              </CardHeader>

              <CardContent>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {Object.entries(
                    data?.counts?.invitation_statuses || {}
                  ).map(
                    ([status, count]) => (
                      <div
                        key={status}
                        className="rounded-lg border p-4"
                      >
                        <StatusBadge value={status} />

                        <div className="mt-3 text-2xl font-bold">
                          {count}
                        </div>
                      </div>
                    )
                  )}

                  {
                    Object.keys(
                      data?.counts?.invitation_statuses || {}
                    ).length === 0
                    && (
                      <p className="text-sm text-muted-foreground">
                        No invitations recorded.
                      </p>
                    )
                  }
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="invitations">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                <div>
                  <CardTitle>
                    Invitation Ledger
                  </CardTitle>

                  <CardDescription>
                    Issue, delivery and candidate-access state.
                  </CardDescription>
                </div>

                <div className="relative w-full xl:w-80">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />

                  <Input
                    value={search}
                    onChange={
                      (event) => setSearch(
                        event.target.value
                      )
                    }
                    placeholder="Search invitations…"
                    className="pl-9"
                  />
                </div>
              </div>
            
              <div className="mt-3 flex justify-end">
                <Button
                  type="button"
                  onClick={openInviteCandidate}
                >
                  Invite Candidate
                </Button>
              </div>
            </CardHeader>

            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="px-3 py-3 font-medium">
                        Candidate
                      </th>

                      <th className="px-3 py-3 font-medium">
                        Invitation
                      </th>

                      <th className="px-3 py-3 font-medium">
                        Delivery
                      </th>

                      <th className="px-3 py-3 font-medium">
                        Issued
                      </th>

                      <th className="px-3 py-3 font-medium">
                        Expiry
                      </th>

                      <th className="px-3 py-3 text-right font-medium">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {invitations.map(
                      (invitation) => (
                        <tr
                          key={invitation.id}
                          className="border-b last:border-0"
                        >
                          <td className="px-3 py-4">
                            <p className="font-medium">
                              {
                                invitation.candidate_name
                                || 'Unnamed candidate'
                              }
                            </p>

                            <p className="text-xs text-muted-foreground">
                              {invitation.candidate_email}
                            </p>
                          </td>

                          <td className="px-3 py-4">
                            <StatusBadge
                              value={invitation.status}
                            />
                          </td>

                          <td className="px-3 py-4">
                            <StatusBadge
                              value={
                                invitation.delivery_status
                                || 'pending'
                              }
                            />
                          </td>

                          <td className="px-3 py-4 text-muted-foreground">
                            {formatDate(
                              invitation.issued_at
                              || invitation.created_at
                            )}
                          </td>

                          <td className="px-3 py-4 text-muted-foreground">
                            {formatDate(
                              invitation.expires_at
                            )}
                          </td>

                          <td className="px-3 py-4">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  runResendPreview(
                                    invitation
                                  )
                                }
                                disabled={
                                  invitation.status
                                  === 'completed'
                                }
                              >
                                <RotateCcw className="mr-1 h-3.5 w-3.5" />
                                Resend Preview
                              </Button>

                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-400"
                                onClick={() =>
                                  setRevokeTarget(
                                    invitation
                                  )
                                }
                                disabled={
                                  ![
                                    'issued',
                                    'opened',
                                    'in_progress',
                                  ].includes(
                                    invitation.status
                                  )
                                }
                              >
                                <Ban className="mr-1 h-3.5 w-3.5" />
                                Revoke
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )
                    )}

                    {invitations.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-3 py-12 text-center text-muted-foreground"
                        >
                          No matching invitations.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="candidates">
          <Card>
            <CardHeader>
              <CardTitle>
                Candidate Progress
              </CardTitle>

              <CardDescription>
                Candidate invitation and secure-session state.
              </CardDescription>
            </CardHeader>

            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="px-3 py-3 font-medium">
                        Candidate
                      </th>

                      <th className="px-3 py-3 font-medium">
                        Contractor
                      </th>

                      <th className="px-3 py-3 font-medium">
                        Invitation
                      </th>

                      <th className="px-3 py-3 font-medium">
                        Session
                      </th>

                      <th className="px-3 py-3 font-medium">
                        Opened
                      </th>

                      <th className="px-3 py-3 font-medium">
                        Completed
                      </th>
                      <th className="px-3 py-3 text-right font-medium">
                        Review
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {candidates.map(
                      (candidate) => (
                        <tr
                          key={candidate.invitation_id}
                          className="border-b last:border-0"
                        >
                          <td className="px-3 py-4">
                              {/* V1.0C-H-R10-C-R1-R1 CLICKABLE CANDIDATE IDENTITY */}
                              <button
                                type="button"
                                className="text-left"
                                onClick={() =>
                                  openCandidateReview(
                                    candidate
                                  )
                                }
                                disabled={reviewLoading}
                              >
                                <p className="font-semibold text-foreground hover:underline">
                                  {
                                    candidate.legal_name
                                    || candidate.candidate_name
                                    || 'Unnamed candidate'
                                  }
                                </p>

                                {
                                  candidate.preferred_name
                                  && (
                                    candidate.preferred_name
                                    !== candidate.legal_name
                                  )
                                  && (
                                    <p className="text-xs text-muted-foreground">
                                      Preferred: {candidate.preferred_name}
                                    </p>
                                  )
                                }

                                <p className="text-xs text-muted-foreground">
                                  {candidate.candidate_email}
                                </p>
                              </button>
                            </td>

                          <td className="px-3 py-4">
                            {
                              candidate.contractor_name
                              || candidate.contractor_id
                            }
                          </td>

                          <td className="px-3 py-4">
                              <StatusBadge
                                value={
                                  candidate.invitation_status
                                }
                              />

                              {
                                candidate.invitation_label
                                && (
                                  <p className="mt-1 max-w-[220px] text-xs text-muted-foreground">
                                    {candidate.invitation_label}
                                  </p>
                                )
                              }
                            </td>

                          <td className="px-3 py-4">
                            {
                              candidate.session_count > 0
                                ? (
                                  <StatusBadge
                                    value={
                                      candidate.session_status
                                    }
                                  />
                                )
                                : (
                                  <span className="text-muted-foreground">
                                    No session
                                  </span>
                                )
                            }
                          </td>

                          <td className="px-3 py-4 text-muted-foreground">
                            {formatDate(
                              candidate.opened_at
                            )}
                          </td>

                          <td className="px-3 py-4 text-muted-foreground">
                            {formatDate(
                              candidate.completed_at
                            )}
                          </td>
                          <td className="px-3 py-4 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                openCandidateReview(
                                  candidate
                                )
                              }
                              disabled={reviewLoading}
                            >
                              <FileSearch className="mr-1 h-3.5 w-3.5" />
                              Review candidate
                            </Button>
                          </td>
                        </tr>
                      )
                    )}

                    {candidates.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-3 py-12 text-center text-muted-foreground"
                        >
                          No candidate records.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

                      <TabsContent value="audit-dashboard" className="mt-6">
                <ContractorOnboardingAuditDashboard />
              </TabsContent>


<TabsContent value="delivery">
          <div className="grid gap-4 xl:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>
                  Delivery Control Plane
                </CardTitle>

                <CardDescription>
                  Operational activation remains separate from the transport master gate.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-5">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border p-4">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      Transport master
                    </p>

                    <div className="mt-2 flex items-center gap-2">
                      {
                        masterEnabled
                          ? (
                            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                          )
                          : (
                            <Clock3 className="h-5 w-5 text-amber-400" />
                          )
                      }

                      <span className="font-semibold">
                        {
                          masterEnabled
                            ? 'Enabled'
                            : 'Locked'
                        }
                      </span>
                    </div>
                  </div>

                  <div className="rounded-lg border p-4">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      Operational gate
                    </p>

                    <div className="mt-2 flex items-center gap-2">
                      {
                        operationalEnabled
                          ? (
                            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                          )
                          : (
                            <Clock3 className="h-5 w-5 text-amber-400" />
                          )
                      }

                      <span className="font-semibold">
                        {
                          operationalEnabled
                            ? 'Enabled'
                            : 'Disabled'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border p-4">
                  <p className="text-sm font-semibold">
                    Rapid X Public Onboarding
                  </p>

                  <p className="mt-1 break-all text-sm text-cyan-400">
                    {data?.public_onboarding_url}
                  </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-lg border p-4">
                    <Gauge className="mb-2 h-4 w-4 text-muted-foreground" />

                    <p className="text-xl font-bold">
                      {
                        data?.operational?.limits
                          ?.max_sends_per_hour
                        ?? 0
                      }
                    </p>

                    <p className="text-xs text-muted-foreground">
                      Per hour
                    </p>
                  </div>

                  <div className="rounded-lg border p-4">
                    <Gauge className="mb-2 h-4 w-4 text-muted-foreground" />

                    <p className="text-xl font-bold">
                      {
                        data?.operational?.limits
                          ?.max_sends_per_day
                        ?? 0
                      }
                    </p>

                    <p className="text-xs text-muted-foreground">
                      Per day
                    </p>
                  </div>

                  <div className="rounded-lg border p-4">
                    <Clock3 className="mb-2 h-4 w-4 text-muted-foreground" />

                    <p className="text-xl font-bold">
                      {
                        data?.operational?.limits
                          ?.duplicate_recipient_window_minutes
                        ?? 0
                      }
                    </p>

                    <p className="text-xs text-muted-foreground">
                      Duplicate window
                    </p>
                  </div>
                </div>

                <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
                  <p className="text-sm font-semibold text-amber-400">
                    G-R1 has not been activated
                  </p>

                  <p className="mt-1 text-sm text-muted-foreground">
                    Operational activation here cannot bypass the locked transport master gate.
                    Production invitation sending remains unavailable until the controlled
                    G-R1 release binds both gates into the send path.
                  </p>
                </div>

                <Button
                  variant={
                    operationalEnabled
                      ? 'destructive'
                      : 'outline'
                  }
                  onClick={previewActivation}
                >
                  <Shield className="mr-2 h-4 w-4" />

                  {
                    operationalEnabled
                      ? 'Preview Deactivation'
                      : 'Preview Operational Activation'
                  }
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  Recipient Eligibility Preview
                </CardTitle>

                <CardDescription>
                  Check contractor, duplicate-recipient and rate-limit policy without sending.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                <div>
                  <p className="mb-2 text-sm font-medium">
                    Contractor
                  </p>

                  <Select
                    value={selectedContractor}
                    onValueChange={setSelectedContractor}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose contractor" />
                    </SelectTrigger>

                    <SelectContent>
                      {(data?.contractors || []).map(
                        (contractor) => (
                          <SelectItem
                            key={contractor.id}
                            value={contractor.id}
                          >
                            {
                              contractor.trading_name
                              || contractor.legal_name
                            }
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <p className="mb-2 text-sm font-medium">
                    Candidate email
                  </p>

                  <Input
                    type="email"
                    value={eligibilityEmail}
                    onChange={
                      (event) =>
                        setEligibilityEmail(
                          event.target.value
                        )
                    }
                    placeholder="candidate@example.com"
                  />
                </div>

                <Button
                  onClick={runEligibility}
                  disabled={
                    eligibilityLoading
                    || !selectedContractor
                    || !eligibilityEmail.trim()
                  }
                >
                  {
                    eligibilityLoading
                      ? (
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      )
                      : (
                        <Gauge className="mr-2 h-4 w-4" />
                      )
                  }

                  Check Eligibility
                </Button>

                {eligibility && (
                  <div
                    className={
                      `rounded-lg border p-4 ${
                        eligibility.eligible
                          ? 'border-emerald-500/30 bg-emerald-500/5'
                          : 'border-amber-500/30 bg-amber-500/5'
                      }`
                    }
                  >
                    <div className="flex items-center gap-2">
                      {
                        eligibility.eligible
                          ? (
                            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                          )
                          : (
                            <AlertTriangle className="h-5 w-5 text-amber-400" />
                          )
                      }

                      <p className="font-semibold">
                        {
                          eligibility.eligible
                            ? 'Eligible'
                            : 'Not currently eligible'
                        }
                      </p>
                    </div>

                    <div className="mt-4 grid gap-2 text-sm">
                      <p>
                        Hourly sends: {eligibility.hourly_send_count}
                        {' / '}
                        {eligibility.hourly_send_limit}
                      </p>

                      <p>
                        Daily sends: {eligibility.daily_send_count}
                        {' / '}
                        {eligibility.daily_send_limit}
                      </p>

                      <p>
                        Recent duplicate:
                        {' '}
                        {
                          eligibility.recent_duplicate
                            ? 'Yes'
                            : 'No'
                        }
                      </p>

                      {
                        eligibility.reasons?.length > 0
                        && (
                          <div>
                            <p className="font-medium">
                              Blocking reasons:
                            </p>

                            <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                              {eligibility.reasons.map(
                                (reason) => (
                                  <li key={reason}>
                                    {reason}
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        )
                      }
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle>
                Immutable Audit History
              </CardTitle>

              <CardDescription>
                Invitation and operational-control events.
              </CardDescription>
            </CardHeader>

            <CardContent>
              <div className="space-y-2">
                {(data?.audit || []).map(
                  (event, index) => (
                    <div
                      key={
                        `${event.audit_source}-${event.created_at}-${index}`
                      }
                      className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-start sm:justify-between"
                    >
                      <div className="flex gap-3">
                        <FileClock className="mt-0.5 h-4 w-4 text-muted-foreground" />

                        <div>
                          <p className="font-medium">
                            {
                              event.event_type
                              || 'audit_event'
                            }
                          </p>

                          <p className="mt-1 text-xs text-muted-foreground">
                            Source: {event.audit_source}
                          </p>

                          {
                            event.actor_id
                            && (
                              <p className="mt-1 text-xs text-muted-foreground">
                                Actor: {event.actor_id}
                              </p>
                            )
                          }
                        </div>
                      </div>

                      <p className="text-xs text-muted-foreground">
                        {formatDate(event.created_at)}
                      </p>
                    </div>
                  )
                )}

                {
                  (data?.audit || []).length === 0
                  && (
                    <p className="py-8 text-center text-sm text-muted-foreground">
                      No audit events.
                    </p>
                  )
                }
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog
        open={Boolean(previewDialog)}
        onOpenChange={
          (open) => {
            if (!open) {
              setPreviewDialog(null);
            }
          }
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {
                previewDialog?.type === 'activate'
                  ? 'Operational Activation'
                  : 'Operational Deactivation'
              }
            </DialogTitle>

            <DialogDescription>
              This changes only the operational gate.
              The transport master gate is not changed by this action.
            </DialogDescription>
          </DialogHeader>

          {previewDialog && (
            <div className="space-y-4">
              <div className="rounded-lg border p-3 text-sm">
                Required confirmation:
                <div className="mt-1 break-words font-mono text-xs">
                  {previewDialog.result?.confirmation_required}
                </div>
              </div>

              <Input
                value={previewDialog.confirmation}
                onChange={
                  (event) =>
                    setPreviewDialog(
                      (current) => ({
                        ...current,
                        confirmation:
                          event.target.value,
                      })
                    )
                }
                placeholder="Type exact confirmation phrase"
              />

              <Input
                value={previewDialog.reason}
                onChange={
                  (event) =>
                    setPreviewDialog(
                      (current) => ({
                        ...current,
                        reason:
                          event.target.value,
                      })
                    )
                }
                placeholder="Operational reason"
              />
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setPreviewDialog(null)
              }
            >
              Cancel
            </Button>

            <Button
              variant={
                previewDialog?.type === 'deactivate'
                  ? 'destructive'
                  : 'default'
              }
              onClick={
                executeOperationalChange
              }
              disabled={
                !previewDialog?.reason?.trim()
                ||
                (
                  previewDialog?.confirmation
                  !==
                  previewDialog?.result
                    ?.confirmation_required
                )
              }
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* ============================================================
          V1.0C-H-R10-C CANDIDATE REVIEW DIALOG
          ============================================================ */}

      <Dialog
        open={Boolean(reviewDialog)}
        onOpenChange={(open) => {
          if (!open) {
            setReviewDialog(null);
            setEvidenceAction(null);
            setEvidenceNotes('');
          }
        }}
      >
        <DialogContent className="max-h-[92vh] max-w-6xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Candidate Review
            </DialogTitle>

            <DialogDescription>
              Review the submitted application, signed acknowledgement
              and supporting evidence before administrative approval.
            </DialogDescription>
          </DialogHeader>

          {reviewDialog && (
            <div className="space-y-6">

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>
                      Candidate
                    </CardDescription>
                  </CardHeader>

                  <CardContent>
                    <p className="font-semibold">
                      {
                        reviewDialog.draft?.steps
                          ?.candidate_profile
                          ?.legal_name
                        || reviewDialog.invitation
                          ?.candidate_name
                        || 'Unnamed candidate' 
                      }
                    </p>

                    <p className="mt-1 text-xs text-muted-foreground">
                      {reviewDialog.invitation?.candidate_email}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>
                      Contractor
                    </CardDescription>
                  </CardHeader>

                  <CardContent>
                    <p className="font-semibold">
                      {
                        reviewDialog.contractor?.trading_name
                        || reviewDialog.contractor?.legal_name
                      }
                    </p>

                    <p className="mt-1 text-xs text-muted-foreground">
                      ABN {reviewDialog.contractor?.abn || '—'}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>
                      Submission
                    </CardDescription>
                  </CardHeader>

                  <CardContent>
                    <StatusBadge
                      value={
                        reviewDialog.invitation?.status
                      }
                    />

                    <p className="mt-2 text-xs text-muted-foreground">
                      {
                        formatDate(
                          reviewDialog.invitation?.completed_at
                        )
                      }
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>
                      Review readiness
                    </CardDescription>
                  </CardHeader>

                  <CardContent>
                    <StatusBadge
                      value={
                        reviewDialog.review?.status
                      }
                    />

                    <p className="mt-2 text-xs text-muted-foreground">
                      {
                        reviewDialog.review?.accepted_required_count
                        ?? 0
                      }
                      /
                      {
                        reviewDialog.review?.required_count
                        ?? 0
                      }
                      {' '}required evidence accepted
                    </p>
                  </CardContent>
                </Card>
              </div>


              <Card>
                <CardHeader>
                  <CardTitle>
                    Submitted Application
                  </CardTitle>

                  <CardDescription>
                    Candidate answers captured at submission.
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2">
                    {
                      Object.entries(
                        reviewDialog.draft?.steps || {}
                      ).map(
                        ([stepName, values]) => (
                          <div
                            key={stepName}
                            className="rounded-lg border p-4"
                          >
                            <p className="mb-3 font-semibold capitalize">
                              {stepName.replaceAll('_', ' ')}
                            </p>

                            <div className="space-y-2">
                              {
                                (
                                  stepName === 'compliance'
                                    ? principalReviewComplianceEntries(
                                        values
                                      )
                                    : Object.entries(
                                        values || {}
                                      )
                                ).map(
                                  /* IRIS_R9_R1_R1_PRINCIPAL_STEP_RENDER */
                                  ([key, value]) => (
                                    <div
                                      key={key}
                                      className="grid grid-cols-2 gap-3 text-sm"
                                    >
                                      <span className="text-muted-foreground">
                                        {key.replaceAll('_', ' ')}
                                      </span>

                                      <span className="break-words text-right">
                                        {
                                          typeof value === 'boolean'
                                            ? (
                                              value
                                                ? 'Yes'
                                                : 'No'
                                            )
                                            : (
                                              value === null
                                              || value === undefined
                                              || value === ''
                                                ? '—'
                                                : String(value)
                                            )
                                        }
                                      </span>
                                    </div>
                                  )
                                )
                              }
                            </div>
                          </div>
                        )
                      )
                    }
                  </div>
                </CardContent>
              </Card>


              <Card>
                <CardHeader>
                  <CardTitle>
                    Signed Acknowledgement & Indemnity
                  </CardTitle>

                  <CardDescription>
                    Immutable candidate execution record.
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  {
                    reviewDialog.acknowledgement
                      ? (
                        <div className="space-y-4">
                          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                            <div>
                              <p className="text-xs text-muted-foreground">
                                Signature
                              </p>

                              <p className="font-medium">
                                {
                                  reviewDialog.acknowledgement.signature_name
                                }
                              </p>
                            </div>

                            <div>
                              <p className="text-xs text-muted-foreground">
                                Executed
                              </p>

                              <p className="font-medium">
                                {
                                  formatDate(
                                    reviewDialog.acknowledgement.executed_at
                                  )
                                }
                              </p>
                            </div>

                            <div>
                              <p className="text-xs text-muted-foreground">
                                Document version
                              </p>

                              <p className="font-medium">
                                {
                                  reviewDialog.acknowledgement.document_version
                                }
                              </p>
                            </div>

                            <div>
                              <p className="text-xs text-muted-foreground">
                                SHA-256
                              </p>

                              <p className="break-all font-mono text-xs">
                                {
                                  reviewDialog.acknowledgement.document_sha256
                                }
                              </p>
                            </div>
                          </div>

                          <div className="max-h-72 overflow-y-auto whitespace-pre-wrap rounded-lg border bg-muted/20 p-4 text-sm">
                            {
                              reviewDialog.acknowledgement.document_text
                              || 'Signed document text unavailable.'
                            }
                          </div>
                        </div>
                      )
                      : (
                        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-400">
                          Signed acknowledgement is unavailable.
                        </div>
                      )
                  }
                </CardContent>
              </Card>


              <Card>
                <CardHeader>
                  <CardTitle>
                    Evidence Review
                  </CardTitle>

                  <CardDescription>
                    Candidate-selected document category is not acceptance.
                    Open each document and verify its actual content before
                    reviewing it.
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4">

                  {
                    (reviewDialog.requirements || []).map(
                      (requirement) => {

                        const evidence =
                          (
                            reviewDialog.current_evidence
                            || []
                          ).find(
                            (item) =>
                              item.document_type
                              === requirement.document_type
                          );

                        return (
                          <div
                            key={requirement.document_type}
                            className="rounded-lg border p-4"
                          >
                            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">

                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="font-semibold">
                                    {
                                      requirement.document_type
                                        .replaceAll('_', ' ')
                                    }
                                  </p>

                                  <StatusBadge
                                    value={
                                      requirement.review_status
                                    }
                                  />
                                </div>

                                {
                                  evidence
                                  ? (
                                    <>
                                      <p className="mt-2 break-all text-sm">
                                        {
                                          evidence.original_filename
                                        }
                                      </p>

                                      <p className="mt-1 text-xs text-muted-foreground">
                                        {
                                          evidence.mime_type
                                        }
                                        {' • '}
                                        {
                                          evidence.size_bytes
                                            ? `${Math.round(
                                                evidence.size_bytes
                                                / 1024
                                              )} KB`
                                            : 'size unavailable'
                                        }
                                      </p>

                                      <p className="mt-2 break-all font-mono text-[11px] text-muted-foreground">
                                        SHA-256: {evidence.sha256}
                                      </p>

                                      {
                                        evidence.review_notes
                                        && (
                                          <div className="mt-3 rounded-md border bg-muted/20 p-3 text-sm">
                                            <span className="font-medium">
                                              Reviewer notes:
                                            </span>
                                            {' '}
                                            {
                                              evidence.review_notes
                                            }
                                          </div>
                                        )
                                      }
                                    </>
                                  )
                                  : (
                                    <p className="mt-2 text-sm text-red-400">
                                      Required evidence is missing.
                                    </p>
                                  )
                                }
                              </div>

                              {
                                evidence
                                && (
                                  <div className="flex shrink-0 flex-wrap gap-2">

                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() =>
                                        viewEvidence(
                                          evidence
                                        )
                                      }
                                    >
                                      <ExternalLink className="mr-1 h-3.5 w-3.5" />
                                      View document
                                    </Button>

                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-emerald-400"
                                      onClick={() =>
                                        beginEvidenceReview(
                                          evidence,
                                          'accepted'
                                        )
                                      }
                                    >
                                      <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                                      Accept
                                    </Button>

                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-red-400"
                                      onClick={() =>
                                        beginEvidenceReview(
                                          evidence,
                                          'rejected'
                                        )
                                      }
                                    >
                                      <XCircle className="mr-1 h-3.5 w-3.5" />
                                      Reject / replacement
                                    </Button>

                                    {
                                      evidence.review_status === 'rejected'
                                      && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() =>
                                            createReplacementLink(
                                              evidence
                                            )
                                          }
                                        >
                                          Create replacement link
                                        </Button>
                                      )
                                    }

                                  </div>
                                )
                              }
                            </div>
                          </div>
                        );
                      }
                    )
                  }

                  {
                    reviewDialog.review?.all_required_accepted
                      ? (
                        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4">
                          <div className="flex items-start gap-3">
                            <ShieldCheck className="mt-0.5 h-5 w-5 text-emerald-400" />

                            <div>
                              <p className="font-semibold text-emerald-400">
                                Evidence review complete
                              </p>

                              <p className="mt-1 text-sm text-muted-foreground">
                                Every required evidence item has been accepted.
                                Candidate is ready for final administrative approval.
                              </p>

                              {
                                reviewDialog.approval
                                  ? (
                                    <div className="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3">
                                      <p className="font-semibold text-emerald-400">
                                        Candidate approved
                                      </p>

                                      <p className="mt-1 text-sm text-muted-foreground">
                                        Workforce admission:
                                        {' '}
                                        {
                                          reviewDialog.approval
                                            ?.workforce_admission
                                            ?.status
                                          || 'admitted'
                                        }
                                      </p>

                                      <p className="mt-1 text-xs text-muted-foreground">
                                        Approved by
                                        {' '}
                                        {
                                          reviewDialog.approval
                                            ?.approved_by
                                          || 'administrator'
                                        }
                                      </p>
                                    </div>
                                  )
                                  : (
                                    <Button
                                      className="mt-4"
                                      onClick={approveCandidateFinal}
                                      disabled={candidateApproving}
                                    >
                                      {
                                        candidateApproving
                                          ? 'Approving…'
                                          : 'Approve candidate'
                                      }
                                    </Button>
                                  )
                              }
                            </div>
                          </div>
                        </div>
                      )
                      : (
                        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-400" />

                            <div>
                              <p className="font-semibold text-amber-400">
                                Administrative review incomplete
                              </p>

                              <p className="mt-1 text-sm text-muted-foreground">
                                Final approval remains unavailable until every
                                required evidence item has been accepted.
                              </p>
                            </div>
                          </div>
                        </div>
                      )
                  }
                </CardContent>
              </Card>


              {/* IRIS_R6_F3_R3_PRE_ACTIVATION_UI */}
              {
                reviewDialog.approval?.status === 'approved'
                && (
                  <Card>
                    <CardHeader>
                      <CardTitle>
                        Operational Identity
                      </CardTitle>

                      <CardDescription>
                        Operational identity provisioning is a
                        separate administrative action after
                        candidate approval and workforce admission.
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      <div className="rounded-lg border bg-muted/20 p-4">
                        <p className="text-sm font-medium">
                          Operational activation boundary
                        </p>

                        <p className="mt-1 text-sm text-muted-foreground">
                          Provisioning an operational identity does
                          not automatically enable roster access,
                          site access, shifts or site assignments.
                        </p>
                      </div>


                      <Button
                        type="button"
                        variant="outline"
                        onClick={
                          loadOperationalEligibility
                        }
                        disabled={
                          operationalEligibilityLoading
                        }
                      >
                        {
                          operationalEligibilityLoading
                            ? 'Checking eligibility…'
                            : 'Check activation eligibility'
                        }
                      </Button>



                      {/* IRIS_R6_F4_RAPIDX_HANDOFF_UI */}

                      {
                        operationalProvisioningError
                        && (
                          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-400">
                            {
                              operationalProvisioningError
                            }
                          </div>
                        )
                      }


                      {
                        (
                          operationalProvisioningResult
                            ?.rapidx_handoff
                            ?.status
                          === 'ready_for_rapidx'
                          ||
                          operationalEligibility
                            ?.rapidx_handoff
                            ?.status
                          === 'ready_for_rapidx'
                        )
                        && (
                          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4">
                            <p className="font-semibold text-emerald-400">
                              Operational identity provisioned
                            </p>

                            <p className="mt-1 text-sm text-muted-foreground">
                              IRIS onboarding is complete and this
                              workforce member is ready for Rapid X
                              integration.
                            </p>

                            <div className="mt-3 rounded-lg border bg-background p-3 text-sm">
                              <p>
                                <span className="text-muted-foreground">
                                  Integration status:
                                </span>
                                {' '}
                                <strong>
                                  Ready for Rapid X
                                </strong>
                              </p>

                              <p className="mt-1">
                                <span className="text-muted-foreground">
                                  Operational workforce:
                                </span>
                                {' '}
                                Managed in Rapid X
                              </p>

                              <p className="mt-1">
                                <span className="text-muted-foreground">
                                  Rostering:
                                </span>
                                {' '}
                                Managed in Rapid X
                              </p>

                              <p className="mt-1">
                                <span className="text-muted-foreground">
                                  Site assignments:
                                </span>
                                {' '}
                                Managed in Rapid X
                              </p>
                            </div>
                          </div>
                        )
                      }


                      {
                        operationalEligibilityError
                        && (
                          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-400">
                            {
                              operationalEligibilityError
                            }
                          </div>
                        )
                      }


                      {
                        operationalEligibility?.classification
                          === 'NEW_IDENTITY'
                        && (
                          <div className="space-y-4">
                            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4">
                              <p className="font-semibold text-emerald-400">
                                Eligible for operational identity
                                provisioning
                              </p>

                              <p className="mt-1 text-sm text-muted-foreground">
                                No existing IRIS operational identity
                                matches this candidate.
                              </p>
                            </div>


                            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                              <div className="rounded-lg border p-4">
                                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                                  Proposed username
                                </p>

                                <p className="mt-1 font-semibold">
                                  {
                                    operationalEligibility
                                      ?.proposed_username
                                    || '—'
                                  }
                                </p>
                              </div>


                              <div className="rounded-lg border p-4">
                                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                                  Operational role
                                </p>

                                <p className="mt-1 font-semibold">
                                  Security Officer
                                </p>
                              </div>


                              <div className="rounded-lg border p-4">
                                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                                  Roster access
                                </p>

                                <p className="mt-1 font-semibold">
                                  Not enabled
                                </p>
                              </div>


                              <div className="rounded-lg border p-4">
                                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                                  Site access
                                </p>

                                <p className="mt-1 font-semibold">
                                  Not enabled
                                </p>
                              </div>
                            </div>


                            <Button
                              type="button"
                              onClick={() =>
                                setOperationalActivationPreviewOpen(
                                  true
                                )
                              }
                            >
                              Prepare operational activation
                            </Button>
                          </div>
                        )
                      }


                      {
                        operationalEligibility?.classification
                          === 'IDENTITY_COLLISION'
                        && (
                          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
                            <p className="font-semibold text-red-400">
                              Identity collision — manual resolution
                              required
                            </p>

                            <p className="mt-1 text-sm text-muted-foreground">
                              An existing IRIS identity matches this
                              candidate. Automatic provisioning is
                              blocked. Existing roles and permissions
                              will not be changed.
                            </p>


                            <div className="mt-4 space-y-2">
                              {
                                (
                                  operationalEligibility
                                    ?.matched_users
                                  || []
                                ).map(
                                  (matchedUser) => (
                                    <div
                                      key={
                                        matchedUser.id
                                        || matchedUser.email
                                      }
                                      className="rounded-lg border bg-background p-3 text-sm"
                                    >
                                      <p>
                                        <span className="text-muted-foreground">
                                          Username:
                                        </span>
                                        {' '}
                                        {
                                          matchedUser.username
                                          || '—'
                                        }
                                      </p>

                                      <p>
                                        <span className="text-muted-foreground">
                                          Role:
                                        </span>
                                        {' '}
                                        {
                                          matchedUser.role
                                          || '—'
                                        }
                                      </p>

                                      <p>
                                        <span className="text-muted-foreground">
                                          Status:
                                        </span>
                                        {' '}
                                        {
                                          matchedUser.status
                                          || '—'
                                        }
                                      </p>
                                    </div>
                                  )
                                )
                              }
                            </div>
                          </div>
                        )
                      }


                      {
                        operationalEligibility
                        && ![
                          'NEW_IDENTITY',
                          'IDENTITY_COLLISION',
                        ].includes(
                          operationalEligibility.classification
                        )
                        && (
                          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
                            <p className="font-semibold text-amber-400">
                              Operational activation unavailable
                            </p>

                            <p className="mt-1 text-sm text-muted-foreground">
                              Classification:
                              {' '}
                              {
                                operationalEligibility
                                  .classification
                                || 'UNKNOWN'
                              }
                            </p>

                            <p className="mt-1 text-sm text-muted-foreground">
                              {
                                operationalEligibility
                                  .classification_reason
                                || 'Manual administrative review is required.'
                              }
                            </p>
                          </div>
                        )
                      }
                    </CardContent>
                  </Card>
                )
              }


              {
                /*
                 * IRIS_R6_F3_R3_R2_R1_PORTALLED_PREVIEW
                 *
                 * Preview only.
                 * Dialog portals outside the candidate-review
                 * DialogContent stacking/overflow context.
                 */
              }

              <Dialog
                open={
                  operationalActivationPreviewOpen
                }
                onOpenChange={
                  setOperationalActivationPreviewOpen
                }
              >
                <DialogContent className="sm:max-w-xl">
                  <DialogHeader>
                    <DialogTitle>
                      Confirm operational identity
                    </DialogTitle>

                    <DialogDescription>
                      Review the proposed identity before a future
                      controlled provisioning transaction.
                    </DialogDescription>
                  </DialogHeader>


                  {
                    operationalEligibility?.classification
                      === 'NEW_IDENTITY'
                    ? (
                      <div className="space-y-4">

                        <div className="rounded-lg border bg-muted/20 p-4">
                          <p className="text-xs uppercase tracking-wide text-muted-foreground">
                            Candidate
                          </p>

                          <p className="mt-1 font-semibold">
                            {
                              operationalEligibility
                                ?.candidate_name
                              || '—'
                            }
                          </p>
                        </div>


                        <div className="grid gap-3 sm:grid-cols-2">

                          <div className="rounded-lg border p-4">
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">
                              Proposed username
                            </p>

                            <p className="mt-1 font-semibold">
                              {
                                operationalEligibility
                                  ?.proposed_username
                                || '—'
                              }
                            </p>
                          </div>


                          <div className="rounded-lg border p-4">
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">
                              Operational role
                            </p>

                            <p className="mt-1 font-semibold">
                              Security Officer
                            </p>
                          </div>


                          <div className="rounded-lg border p-4">
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">
                              Roster access
                            </p>

                            <p className="mt-1 font-semibold">
                              Not enabled
                            </p>
                          </div>


                          <div className="rounded-lg border p-4">
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">
                              Site access
                            </p>

                            <p className="mt-1 font-semibold">
                              Not enabled
                            </p>
                          </div>

                        </div>


                        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">

                          <p className="font-semibold text-amber-400">
                            Provisioning disabled for visual acceptance
                          </p>

                          <p className="mt-1 text-sm text-muted-foreground">
                            No operational account will be created
                            during this acceptance gate.

                            Roster access and site access remain
                            independently controlled.
                          </p>

                        </div>

                      </div>
                    )
                    : (
                      <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">

                        <p className="font-semibold text-red-400">
                          Eligibility is no longer valid
                        </p>

                        <p className="mt-1 text-sm text-muted-foreground">
                          Close this dialog and check activation
                          eligibility again.
                        </p>

                      </div>
                    )
                  }


                  <DialogFooter>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        setOperationalActivationPreviewOpen(
                          false
                        )
                      }
                    >
                      Cancel
                    </Button>


                    {/*
                     * IRIS_R6_F3_R3_PROVISIONING_DORMANT
                     *
                     * Deliberately disabled.
                     * No provisioning POST exists here.
                     */}
                    {/*
                     * IRIS_R6_F4_PROVISION_ENABLED
                     *
                     * Explicit principal-controlled transaction.
                     */}
                    <Button
                      type="button"
                      onClick={
                        confirmOperationalIdentityProvisioning
                      }
                      disabled={
                        operationalProvisioning
                        || operationalEligibility
                          ?.classification
                          !== 'NEW_IDENTITY'
                      }
                    >
                      {
                        operationalProvisioning
                          ? 'Provisioning…'
                          : 'Provision identity'
                      }
                    </Button>

                  </DialogFooter>

                </DialogContent>
              </Dialog>

            </div>
          )}




          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setReviewDialog(null)
              }
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog
        open={Boolean(evidenceAction)}
        onOpenChange={(open) => {
          if (!open) {
            setEvidenceAction(null);
            setEvidenceNotes('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {
                evidenceAction?.status === 'accepted'
                  ? 'Accept evidence'
                  : 'Reject evidence / request replacement'
              }
            </DialogTitle>

            <DialogDescription>
              {
                evidenceAction?.evidence?.original_filename
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <label className="text-sm font-medium">
              Reviewer notes
              {
                evidenceAction?.status === 'rejected'
                  ? ' — required'
                  : ' — optional'
              }
            </label>

            <textarea
              value={evidenceNotes}
              onChange={
                (event) =>
                  setEvidenceNotes(
                    event.target.value
                  )
              }
              rows={5}
              maxLength={1000}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              placeholder={
                evidenceAction?.status === 'rejected'
                  ? 'Explain what is wrong and what replacement is required.'
                  : 'Optional verification notes.'
              }
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEvidenceAction(null);
                setEvidenceNotes('');
              }}
              disabled={evidenceSubmitting}
            >
              Cancel
            </Button>

            <Button
              onClick={submitEvidenceReview}
              disabled={evidenceSubmitting}
            >
              {
                evidenceSubmitting
                  ? 'Saving…'
                  : (
                    evidenceAction?.status === 'accepted'
                      ? 'Confirm acceptance'
                      : 'Confirm rejection'
                  )
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* ============================================================= */}
      {/* V1.0C-H-R10-J-R2 INVITE CANDIDATE DIALOG                    */}
      {/* ============================================================= */}

      <Dialog
        open={inviteDialogOpen}
        onOpenChange={(open) => {
          if (!open && !inviteSubmitting) {
            setInviteDialogOpen(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Invite Candidate
            </DialogTitle>

            <DialogDescription>
              Create a secure contractor onboarding invitation using
              the existing IRIS invitation engine.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">

            <div className="space-y-2">
              <label
                htmlFor="invite-candidate-name"
                className="text-sm font-medium"
              >
                Candidate name
              </label>

              <Input
                id="invite-candidate-name"
                value={inviteCandidateName}
                onChange={(event) =>
                  setInviteCandidateName(
                    event.target.value
                  )
                }
                placeholder="Full name"
                maxLength={200}
                autoComplete="name"
                disabled={inviteSubmitting}
              />

              <p className="text-xs text-muted-foreground">
                Optional. This will identify the candidate in the
                onboarding administration console.
              </p>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="invite-candidate-email"
                className="text-sm font-medium"
              >
                Candidate email
              </label>

              <Input
                id="invite-candidate-email"
                type="email"
                value={inviteCandidateEmail}
                onChange={(event) =>
                  setInviteCandidateEmail(
                    event.target.value
                  )
                }
                placeholder="candidate@example.com"
                autoComplete="email"
                disabled={inviteSubmitting}
              />

              <p className="text-xs text-muted-foreground">
                Required. The invitation will be issued to this
                email address.
              </p>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="invite-ttl-hours"
                className="text-sm font-medium"
              >
                Invitation validity
              </label>

              <select
                id="invite-ttl-hours"
                value={inviteTtlHours}
                onChange={(event) =>
                  setInviteTtlHours(
                    Number(event.target.value)
                  )
                }
                disabled={inviteSubmitting}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value={24}>
                  24 hours
                </option>

                <option value={48}>
                  48 hours
                </option>

                <option value={72}>
                  72 hours
                </option>

                <option value={120}>
                  5 days
                </option>

                <option value={168}>
                  7 days
                </option>
              </select>

              <p className="text-xs text-muted-foreground">
                The existing invitation engine permits a validity
                period from 1 to 168 hours.
              </p>
            </div>

            <div className="rounded-lg border bg-muted/30 p-4">
              <p className="text-sm font-medium">
                Invitation control
              </p>

              <p className="mt-1 text-xs text-muted-foreground">
                Rapid X will create a preview invitation ledger record only. No email will be sent.
              </p>
            </div>

          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                setInviteDialogOpen(false)
              }
              disabled={inviteSubmitting}
            >
              Cancel
            </Button>

            <Button
              type="button"
              onClick={submitCandidateInvitation}
              disabled={
                inviteSubmitting
                || !inviteCandidateEmail?.trim()
              }
            >
              {
                inviteSubmitting
                  ? 'Creating preview…'
                  : 'Create Preview Invitation'
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      <Dialog
        open={Boolean(revokeTarget)}
        onOpenChange={
          (open) => {
            if (!open) {
              setRevokeTarget(null);
              setRevokeReason('');
            }
          }
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Revoke Invitation
            </DialogTitle>

            <DialogDescription>
              This permanently invalidates the selected invitation.
              It does not send another email.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border p-4">
              <p className="font-medium">
                {revokeTarget?.candidate_name}
              </p>

              <p className="text-sm text-muted-foreground">
                {revokeTarget?.candidate_email}
              </p>
            </div>

            <Input
              value={revokeReason}
              onChange={
                (event) =>
                  setRevokeReason(
                    event.target.value
                  )
              }
              placeholder="Reason for revocation"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setRevokeTarget(null)
              }
            >
              Cancel
            </Button>

            <Button
              variant="destructive"
              onClick={confirmRevoke}
            >
              Revoke Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(resendResult)}
        onOpenChange={
          (open) => {
            if (!open) {
              setResendResult(null);
            }
          }
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Resend Preview
            </DialogTitle>

            <DialogDescription>
              Preview only. No invitation email has been sent.
            </DialogDescription>
          </DialogHeader>

          <pre className="max-h-96 overflow-auto rounded-lg border bg-muted/30 p-4 text-xs">
            {
              JSON.stringify(
                resendResult?.result,
                null,
                2
              )
            }
          </pre>

          <DialogFooter>
            <Button
              onClick={() =>
                setResendResult(null)
              }
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
